import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, getToken, setToken, clearToken, formatErr } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = user
  const [user, setUser] = useState(null);

  useEffect(() => {
    let cancel = false;
    const t = getToken();
    if (!t) {
      setUser(false);
      return undefined;
    }
    api
      .get("/auth/me")
      .then((r) => { if (!cancel) setUser(r.data); })
      .catch((err) => {
        if (cancel) return;
        // Token invalid/expired: clear and force re-login
        if (err?.response?.status !== 401 && process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("Auth check failed:", err?.message || err);
        }
        clearToken();
        setUser(false);
      });
    return () => { cancel = true; };
    // Mount-only effect: api/getToken/clearToken/setUser are stable module-level refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) setToken(data.token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Logout request failed (clearing local session anyway):", err?.message || err);
      }
    }
    clearToken();
    setUser(false);
  }, []);

  // Stable context value — only changes when user changes (login/logout/setUser are useCallback'd).
  const value = useMemo(() => ({ user, login, logout, setUser }), [user, login, logout]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
